@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import com.taocent.simple.compose.component.blockrichtext.internal.block.AnchorPosition
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor

/**
 * BlockState 焦点/锚点光标相关的扩展函数。
 *
 * 涵盖文本块焦点、表格单元格焦点、BlockAnchor 锚点光标、
 * 焦点恢复(包含按 index 寻找最近文本块并恢复)。
 */

// region 私有 setter helpers

internal fun BlockState.setTextFocusOnly(blockId: String, requestFocus: Boolean = false) {
    focusedBlockId = blockId
    if (requestFocus) {
        requestFocusedBlockFocus()
    }
}

internal fun BlockState.setFocusedBlockCompat(blockId: String, requestFocus: Boolean = false) {
    focusedBlockId = blockId
    val block = _blocks.firstOrNull { it.id == blockId }
    if (block is TextBlock) {
        val state = getBlockState(blockId)
        val selection = state.textFieldValue.selection
        editorCursor = EditorCursor.Text(blockId, selection.start, selection.end)
    }
    if (requestFocus) {
        requestFocusedBlockFocus()
    }
}

internal fun BlockState.setTextCursor(blockId: String, requestFocus: Boolean = false) {
    setTextFocusOnly(blockId, requestFocus)
    val state = getBlockState(blockId)
    val selection = state.textFieldValue.selection
    editorCursor = EditorCursor.Text(blockId, selection.start, selection.end)
}

internal fun BlockState.setTableCellCursor(
    tableId: String,
    rowId: String,
    cellId: String,
    cellState: com.taocent.simple.compose.component.richtext.core.RichTextState,
    focusRequester: androidx.compose.ui.focus.FocusRequester
) {
    focusedTableCellState = cellState
    focusedTableCellFocusRequester = focusRequester
    editorCursor = EditorCursor.TableCell(tableId, rowId, cellId)
}

internal fun BlockState.clearTableCellFocus() {
    focusedTableCellState = null
    focusedTableCellFocusRequester = null
}

internal fun BlockState.setBlockAnchorCursor(
    blockId: String,
    position: AnchorPosition = AnchorPosition.Before
) {
    navSelectedTableId = blockId
    editorCursor = EditorCursor.BlockAnchor(blockId, position)
}

internal fun BlockState.setLegacyTableCellFocus(
    cellState: com.taocent.simple.compose.component.richtext.core.RichTextState,
    focusRequester: androidx.compose.ui.focus.FocusRequester
) {
    focusedTableCellState = cellState
    focusedTableCellFocusRequester = focusRequester
}

// endregion

// region 焦点恢复

internal fun BlockState.restoreTextFocusIfNeeded(blockId: String?) {
    if (blockId != null && _blocks.any { it.id == blockId && it is TextBlock }) {
        if (editorCursor is EditorCursor.BlockAnchor) {
            setTextFocusOnly(blockId, requestFocus = true)
        } else {
            setTextCursor(blockId, requestFocus = true)
        }
    }
}

internal fun BlockState.restoreNearestTextFocus(anchorIndex: Int) {
    val blockId = findNearestTextBlockId(anchorIndex) ?: return
    if (editorCursor is EditorCursor.BlockAnchor) {
        setTextFocusOnly(blockId, requestFocus = true)
    } else {
        setTextCursor(blockId, requestFocus = true)
    }
}

// endregion

// region 活动 RichTextState 解析(Cmd/Ctrl+V / 浮动工具栏 onPaste 等"按当前焦点 paste" 路径)

/**
 * 解析"当前活动 RichTextState",用于 paste / IME 拦截等"按当前焦点写入"路径。
 *
 * **优先级**(与 [BlockState] 内部 [activeRichTextState] 一致,但**安全 fallback**):
 *   1. [BlockState.focusedTableCellState] 非空 → 表格单元格 state
 *   2. [BlockState.focusedBlockId] 对应的 TextBlock state([BlockState.getBlockState] 自动 lazy 创建)
 *   3. [BlockState.blocks] 第一个 block state
 *   4. 全部为空 → null(blocks 列表为空时不创建垃圾 state,避免污染 [BlockState.blockStates] map)
 *
 * **为什么不用 [BlockState.focusedBlockState] getter**:在 cell 焦点期间
 * `focusedBlockId` 仍指向**上次** TextBlock(只有 `setTextCursor` 才更新),
 * 直接用 `getBlockState(focusedBlockId)` 会粘贴到 TextBlock 而不是 cell ——
 * 这是 IME 拦截改造前未触发的 latent bug,**[BlockRichTextEditor] 顶层 Cmd/Ctrl+V
 * 拦截与浮动工具栏 onPaste 都依赖本函数**。
 *
 * **为什么不用 [BlockState.activeRichTextState] private getter**:它内部
 * `focusedBlockState` 在 `focusedBlockId` 为空时会调 `getBlockState("")` 创建**空
 * RichTextState** 并写入 `blockStates[""]`,污染 map。本函数额外加 `blocks.firstOrNull()`
 * 兜底,避免空 state 被创建。
 */
internal fun resolveActiveRichTextState(state: BlockState): com.taocent.simple.compose.component.richtext.core.RichTextState? {
    // 1) cell focus 优先
    state.focusedTableCellState?.let { return it }
    // 2) focusedBlockId 对应的 TextBlock
    val focusedId = state.focusedBlockId
    if (focusedId.isNotEmpty() && state._blocks.any { it.id == focusedId && it is TextBlock }) {
        return state.getBlockState(focusedId)
    }
    // 3) fallback 到第一个 TextBlock,避免空 state
    val firstTextId = state.blocks.firstOrNull { it is TextBlock }?.id
    if (firstTextId != null) {
        return state.getBlockState(firstTextId)
    }
    // 4) 没有 TextBlock(空文档)
    return null
}

// endregion
